import { Dialog } from '@cimplico/components';
import { toastNotification } from '../../components/toast/Toast';
import { useIncludeWorksheets } from '../../queries/use-include-worksheets';
import plural from '../../utilities/plural';

type WorksheetIncludeConfirmProps = {
    worksheetIds: string[];
    onClose: () => void;
    onInclude: () => void;
    onError?: (errorMessages: { [id: string]: string }) => void;
};

function WorksheetIncludeConfirm({ worksheetIds, onClose, onInclude, onError }: WorksheetIncludeConfirmProps): JSX.Element {
    const descriptionStart = `Do you want to include ${plural(
        worksheetIds.length,
        'this worksheet',
        `${worksheetIds.length} worksheets`
    )}?`;

    const includeWorksheetsQuery = useIncludeWorksheets();

    function include(): void {
        includeWorksheetsQuery.mutate(
            worksheetIds.map((id) => ({ id })),
            {
                onSuccess: () => {
                    onInclude();
                    toastNotification(
                        'success',
                        `${worksheetIds.length} ${plural(worksheetIds.length, 'worksheet', 'worksheets')} marked as included!`
                    );
                },

                onError: (err) => {
                    const results = err.response?.data?.results ?? [];

                    if (worksheetIds.length === 1) {
                        toastNotification('error', 'Unable to include this worksheet!', results[0].error?.fields?.errorMessage);
                        onClose();
                        return;
                    }

                    const errors = results.filter((r: { statusCode: number }) => r.statusCode >= 400);
                    const success = results.filter((r: { statusCode: number }) => r.statusCode < 400);

                    if (success.length > 0) {
                        toastNotification(
                            'success',
                            `${success.length} ${plural(success.length, 'worksheet', 'worksheets')} marked as included!`
                        );
                    }

                    toastNotification('error', `Unable to include ${errors.length} ${plural(errors.length, 'worksheet', 'worksheets')}!`);

                    const errorMessages: { [id: string]: string } = {};

                    for (const err of errors) {
                        errorMessages[err.error.fields.id] = err.error.fields.errorMessage;
                    }

                    onError && onError(errorMessages);
                    onClose();
                },
            }
        );
    }

    return (
        <Dialog
            title={`Include Worksheet${worksheetIds.length > 1 ? 's' : ''}`}
            type="info"
            description={`${descriptionStart} Included worksheets count towards a workpaper's progress.`}
            isOpen
            primaryAction="Include"
            secondaryAction="Cancel"
            onPrimaryActionClick={include}
            onClose={onClose}
            onSecondaryActionClick={onClose}
            isLoading={includeWorksheetsQuery.isLoading}
        />
    );
}
export default WorksheetIncludeConfirm;
